import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth, getLastEmail } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { ArrowRight, Shirt, Camera, TrendingUp, DollarSign, Gift } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function LandingPage() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [referrerName, setReferrerName] = useState(null);
  const [referralCode, setReferralCode] = useState(null);

  // Auto-redirect to dashboard if already logged in
  useEffect(() => {
    if (!loading && user) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    // Check for referral code in URL
    const refCode = searchParams.get("ref");
    if (refCode) {
      setReferralCode(refCode);
      localStorage.setItem("wornwell_referral_code", refCode);
      
      // Validate and get referrer name
      fetch(`${API_URL}/api/referral/validate/${refCode}`)
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data?.valid) {
            setReferrerName(data.referrer_name);
          }
        })
        .catch(console.error);
    } else {
      // Check localStorage for previously stored code
      const storedCode = localStorage.getItem("wornwell_referral_code");
      if (storedCode) {
        setReferralCode(storedCode);
      }
    }
  }, [searchParams]);

  // Handle login with email hint for returning users
  const handleLogin = () => {
    const lastEmail = getLastEmail();
    login(lastEmail);
  };

  // Show loading state while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // If user is logged in, they'll be redirected - show nothing briefly
  if (user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Safe area spacer for PWA standalone mode */}
      <div className="h-[env(safe-area-inset-top,0px)] bg-background w-full fixed top-0 left-0 right-0 z-[60]" />
      
      {/* Navigation */}
      <header className="fixed top-[env(safe-area-inset-top,0px)] w-full z-50 bg-background/80 backdrop-blur-sm border-b border-border">
        <div className="container mx-auto px-6 md:px-12">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-heading text-xl font-semibold tracking-tight">WornWell</span>
              <span className="text-xs text-muted-foreground">.io</span>
            </div>
            <Button 
              onClick={handleLogin} 
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm h-10 px-6 font-medium tracking-wide text-sm"
              data-testid="login-btn"
            >
              Sign In
            </Button>
          </div>
        </div>
      </header>

      {/* Referral Banner */}
      {referrerName && (
        <div className="fixed top-[calc(4rem+env(safe-area-inset-top,0px))] left-0 right-0 z-40 bg-primary text-white py-3 px-4 text-center text-sm font-medium">
          <Gift className="w-4 h-4 inline-block mr-2" />
          <span>{referrerName} invited you to WornWell</span>
        </div>
      )}

      {/* Hero Section - Clean, generous whitespace */}
      <section className={`pb-24 md:pb-40 ${referrerName ? 'pt-[calc(10rem+env(safe-area-inset-top,0px))] md:pt-48' : 'pt-[calc(7rem+env(safe-area-inset-top,0px))] md:pt-36'}`}>
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <div className="space-y-10 animate-fade-in">
              <div className="space-y-6">
                <p className="text-xs uppercase tracking-wide font-medium text-muted-foreground">
                  WornWell
                </p>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-[3.5rem] tracking-tight text-primary leading-[1.1]">
                  Know what you wear.<br />Know what it's worth.
                </h1>
              </div>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-md">
                Track cost-per-wear, rediscover forgotten pieces, and make smarter wardrobe decisions.
              </p>
              <div className="pt-2">
                <Button 
                  onClick={handleLogin}
                  size="lg"
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full h-14 px-10 font-medium text-sm"
                  data-testid="get-started-btn"
                >
                  Get Started
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <p className="text-xs text-muted-foreground mt-3 max-w-xs">
                  By creating an account, you agree to our{' '}
                  <button onClick={handleLogin} className="underline hover:text-primary">Terms of Service</button>
                  {' '}and{' '}
                  <button onClick={handleLogin} className="underline hover:text-primary">Privacy Policy</button>
                </p>
              </div>
            </div>
            <div className="relative animate-fade-in animate-delay-200">
              <div className="aspect-[4/5] relative overflow-hidden rounded-lg">
                <img
                  src="https://customer-assets.emergentagent.com/job_wardrobe-value/artifacts/bav3pxi4_Martin%20Brule.jpeg"
                  alt="Luxury wardrobe"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Floating stats card - minimal */}
              <div className="absolute -bottom-6 -left-6 bg-white border border-border/50 p-5 rounded-lg shadow-sm max-w-[180px]">
                <p className="text-xs text-muted-foreground mb-1">Avg Cost Per Wear</p>
                <p className="font-display text-2xl text-primary">$4.50</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - Clean grid */}
      <section className="py-24 md:py-36 bg-muted/30">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-16 md:mb-20">
            <h2 className="font-display text-2xl md:text-3xl tracking-tight text-primary">
              How it works
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {[
              {
                icon: Shirt,
                title: "Add items",
                description: "Build your virtual wardrobe with photos, costs, and brands."
              },
              {
                icon: Camera,
                title: "Log outfits",
                description: "Snap your daily look. AI identifies what you're wearing."
              },
              {
                icon: DollarSign,
                title: "Track value",
                description: "See cost-per-wear drop with every outfit you log."
              },
              {
                icon: TrendingUp,
                title: "Get insights",
                description: "Discover patterns and optimize your wardrobe."
              }
            ].map((feature, index) => (
              <div 
                key={feature.title}
                className={`text-center animate-fade-in animate-delay-${(index + 1) * 100}`}
              >
                <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-5">
                  <feature.icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-heading text-lg text-primary mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Minimal */}
      <section className="py-24 md:py-36">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-display text-3xl md:text-4xl tracking-tight text-primary mb-6">
              Start wearing smarter
            </h2>
            <p className="text-lg text-muted-foreground mb-10">
              Join thousands who've discovered the true value of their wardrobe.
            </p>
            <Button 
              onClick={handleLogin}
              size="lg"
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full h-14 px-10 font-medium text-sm"
              data-testid="cta-get-started-btn"
            >
              Get Started
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer - With legal links */}
      <footer className="py-8 border-t border-border/50">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <div className="flex flex-col gap-6">
            {/* Legal Links */}
            <div className="flex flex-wrap justify-center gap-4 md:gap-6 text-xs text-muted-foreground">
              <button onClick={handleLogin} className="hover:text-primary transition-colors">
                Privacy Policy
              </button>
              <button onClick={handleLogin} className="hover:text-primary transition-colors">
                Terms of Service
              </button>
              <button onClick={handleLogin} className="hover:text-primary transition-colors">
                Cookie Policy
              </button>
              <button onClick={handleLogin} className="hover:text-primary transition-colors">
                Disclaimer
              </button>
            </div>
            
            {/* Copyright */}
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="flex items-center gap-1">
                <span className="font-heading text-base">WornWell</span>
              </div>
              <p className="text-xs text-muted-foreground">
                © 2025 WornWell. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
